import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Markdown from 'components/Shared/Markdown';
import FormattingGuideDrawer from 'components/TextProjects/FormattingGuideDrawer/FormattingGuideDrawer';
import styles from './ProjectDescriptionField.module.scss';

/**
 * Project description with Write / Preview tabs and an on-demand formatting guide.
 * Preview uses the same Markdown + embed pipeline as the live project page.
 */
const ProjectDescriptionField = ({ value, onChange, disabled, error }) => {
  const [mode, setMode] = useState('write');
  const [guideOpen, setGuideOpen] = useState(false);
  const rootRef = useRef(null);
  const textareaRef = useRef(null);
  const guideTriggerRef = useRef(null);
  const selectionRef = useRef({ start: null, end: null, scrollTop: 0 });
  const previousModeRef = useRef(mode);

  const saveTextareaState = useCallback(() => {
    const el = textareaRef.current;
    if (!el) {
      return;
    }
    selectionRef.current = {
      start: el.selectionStart,
      end: el.selectionEnd,
      scrollTop: el.scrollTop,
    };
  }, []);

  const setWriteMode = useCallback(() => {
    setMode('write');
  }, []);

  const setPreviewMode = useCallback(() => {
    saveTextareaState();
    setMode('preview');
  }, [saveTextareaState]);

  useEffect(() => {
    const wasPreview = previousModeRef.current === 'preview';
    previousModeRef.current = mode;

    if (mode !== 'write' || !wasPreview) {
      return;
    }

    const el = textareaRef.current;
    if (!el) {
      return;
    }

    const { start, end, scrollTop } = selectionRef.current;
    el.focus();
    el.scrollTop = scrollTop;
    if (start != null && end != null) {
      try {
        el.setSelectionRange(start, end);
      } catch (e) {
        // Ignore if selection restore fails (e.g. value shrunk).
      }
    }
  }, [mode]);

  const openGuide = useCallback(() => {
    setGuideOpen(true);
  }, []);

  const closeGuide = useCallback(() => {
    setGuideOpen(false);
  }, []);

  const hasContent = Boolean(value && value.trim());

  return (
    <div className={styles.root} ref={rootRef}>
      <div className={styles.header}>
        <label className={styles.label} htmlFor="project-description">
          Description
        </label>
        <div
          className={styles.tabs}
          role="tablist"
          aria-label="Description mode"
        >
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'write'}
            className={mode === 'write' ? styles.tabActive : styles.tab}
            onClick={setWriteMode}
            disabled={disabled}
          >
            Write
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'preview'}
            className={mode === 'preview' ? styles.tabActive : styles.tab}
            onClick={setPreviewMode}
            disabled={disabled}
          >
            Preview
          </button>
        </div>
      </div>

      {mode === 'write' ? (
        <textarea
          ref={textareaRef}
          id="project-description"
          className={styles.textarea}
          value={value}
          placeholder="What was the brief, what did you make, what was the result?"
          onChange={(event) => onChange(event.target.value)}
          onSelect={saveTextareaState}
          onBlur={saveTextareaState}
          onScroll={saveTextareaState}
          disabled={disabled}
          rows={6}
        />
      ) : (
        <div className={styles.previewWrap}>
          <div className={styles.preview}>
            {hasContent ? (
              <Markdown text={value} />
            ) : (
              <p className={styles.previewEmpty}>Nothing to preview yet.</p>
            )}
          </div>
          {hasContent && (
            <p className={styles.previewCaption}>
              This is how the description will look on the project page.
            </p>
          )}
        </div>
      )}

      <div className={styles.footer}>
        <p className={styles.footerHelp}>Markdown and embeds work here.</p>
        <button
          ref={guideTriggerRef}
          type="button"
          className={styles.footerLink}
          onClick={openGuide}
          disabled={disabled}
        >
          Formatting guide
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <FormattingGuideDrawer
        open={guideOpen}
        onClose={closeGuide}
        triggerRef={guideTriggerRef}
        retainRef={rootRef}
      />
    </div>
  );
};

ProjectDescriptionField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string,
};

ProjectDescriptionField.defaultProps = {
  value: '',
  disabled: false,
  error: null,
};

export default ProjectDescriptionField;
